import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LeadProvider } from './contexts/LeadContext';
import LoginForm from './components/Auth/LoginForm';
import Header from './components/Layout/Header';
import LeadList from './components/Leads/LeadList';
import LeadForm from './components/Leads/LeadForm';
import ContactLogModal from './components/Contacts/ContactModal';
import AIAssistant from './components/AI/AIAssistant';
import SettingsModal from './components/Settings/SettingsModal';
import ReportsModal from './components/Reports/ReportsModal';
import LeadReportModal from './components/Reports/LeadReportModal';
import ChangePasswordModal from './components/Auth/ChangePasswordModal';
import UserManagementModal from './components/Admin/UserManagementModal';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';

const { FiPlus, FiDownload, FiBarChart3 } = FiIcons;

const Dashboard = () => {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showContactLogModal, setShowContactLogModal] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showLeadReport, setShowLeadReport] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [aiSettings, setAiSettings] = useState({
    provider: 'openai',
    openai_model: 'gpt-3.5-turbo',
    straico_api_key: '',
    straico_model: 'YOUR_MODEL_IDENTIFIER'
  });

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadForm(true);
  };

  const handleShowContacts = (lead) => {
    setSelectedLead(lead);
    setShowContactLogModal(true);
  };

  const handleGenerateContent = (lead) => {
    setSelectedLead(lead);
    setShowAIAssistant(true);
  };

  const handleShowLeadReport = (lead) => {
    setSelectedLead(lead);
    setShowLeadReport(true);
  };

  const handleCloseModals = () => {
    setShowLeadForm(false);
    setShowContactLogModal(false);
    setShowAIAssistant(false);
    setShowSettings(false);
    setShowReports(false);
    setShowLeadReport(false);
    setShowChangePassword(false);
    setShowUserManagement(false);
    setSelectedLead(null);
  };

  const handleApiKeyChange = (newApiKey) => {
    setApiKey(newApiKey);
  };

  const handleAiSettingsChange = (newSettings) => {
    setAiSettings(newSettings);
  };

  return (
    <div className="app-container">
      <Header
        onShowSettings={() => setShowSettings(true)}
        onShowChangePassword={() => setShowChangePassword(true)}
        onShowUserManagement={() => setShowUserManagement(true)}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Lead Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowReports(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiBarChart3} className="h-5 w-5" />
              <span>Reports</span>
            </button>
            <button
              onClick={() => setShowLeadForm(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-electric-600 text-white rounded-lg hover:bg-electric-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="h-5 w-5" />
              <span>Add Lead</span>
            </button>
          </div>
        </div>

        <LeadList
          onEditLead={handleEditLead}
          onShowContacts={handleShowContacts}
          onGenerateContent={handleGenerateContent}
          onShowReport={handleShowLeadReport}
        />
      </main>

      {/* Modals */}
      {showLeadForm && (
        <LeadForm
          lead={selectedLead}
          onClose={handleCloseModals}
        />
      )}

      {showContactLogModal && selectedLead && (
        <ContactLogModal
          lead={selectedLead}
          onClose={handleCloseModals}
          onGenerateContent={handleGenerateContent}
          onShowReport={() => {
            setShowContactLogModal(false);
            setShowLeadReport(true);
          }}
        />
      )}

      {showAIAssistant && selectedLead && (
        <AIAssistant
          lead={selectedLead}
          onClose={handleCloseModals}
          apiKey={apiKey}
          aiSettings={aiSettings}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={handleCloseModals}
          onApiKeyChange={handleApiKeyChange}
          onAiSettingsChange={handleAiSettingsChange}
          currentApiKey={apiKey}
          currentAiSettings={aiSettings}
        />
      )}

      {showReports && (
        <ReportsModal
          onClose={handleCloseModals}
        />
      )}

      {showLeadReport && selectedLead && (
        <LeadReportModal
          lead={selectedLead}
          onClose={handleCloseModals}
        />
      )}

      {showChangePassword && (
        <ChangePasswordModal
          onClose={handleCloseModals}
        />
      )}

      {showUserManagement && (
        <UserManagementModal
          onClose={handleCloseModals}
        />
      )}
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <LeadProvider>
                  <Dashboard />
                </LeadProvider>
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;